import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  CircularProgress,
  Drawer,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon,
  FolderOpen as FolderOpenIcon,
  LocalPrintshopOutlined as LocalPrintshopIcon,
  OpenInNew as OpenInNewIcon,
  TaskAlt as TaskAltIcon,
} from '@mui/icons-material';
import { GlobalButton } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import {
  BUSINESS_ROLE_GROUPS,
  EXTERNAL_OPERATOR_ROLE_NAMES,
  ROLES,
  userHasPermission,
} from '@/components/security/AppRoles';
import { useUserInfo } from '@/hooks';
import type { BatchLabelJobState } from '../hooks/useBatchLabelPrint';

export interface BatchLabelProgressDrawerProps {
  job: BatchLabelJobState | null;
  onClose: () => void;
}

const formatElapsed = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0
    ? `${minutes} min ${String(seconds).padStart(2, '0')} s`
    : `${seconds} s`;
};

const labelCountText = (count: number): string =>
  count === 1 ? '1 etiqueta' : `${count} etiquetas`;

/**
 * Panel lateral que acompaña la generación del PDF combinado de etiquetas.
 * Reemplaza la pestaña en blanco que se abría antes: acá el progreso se ve
 * dentro de la app y el PDF se abre con un click explícito, que ningún
 * bloqueador de popups rechaza.
 */
const BatchLabelProgressDrawer = ({
  job,
  onClose,
}: BatchLabelProgressDrawerProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { permissions } = useUserInfo();
  const [elapsedMs, setElapsedMs] = useState(0);

  const isCommerceModule = pathname.startsWith('/business-commerces');
  const isExternalOperator = userHasPermission(
    permissions,
    EXTERNAL_OPERATOR_ROLE_NAMES as ROLES[]
  );
  const jobsPath = isCommerceModule
    ? '/business-commerces/jobs'
    : isExternalOperator
      ? '/business-external-operators/jobs'
      : '/business/jobs';
  const canSeeJobs =
    isCommerceModule ||
    isExternalOperator ||
    userHasPermission(permissions, BUSINESS_ROLE_GROUPS.full);
  const isRunning = job?.phase === 'running';

  useEffect(() => {
    if (!job || !isRunning) return undefined;
    const tick = () => setElapsedMs(Date.now() - job.startedAtMs);
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [job, isRunning]);

  if (!job) return null;

  const actionNoun = job.mode === 'print' ? 'imprimir' : 'descargar';
  const failedShippings = job.failedShippings;
  const generatedCount = Math.max(
    0,
    job.requestedCount - failedShippings.length
  );

  return (
    <Drawer
      anchor='right'
      open
      onClose={onClose}
      slotProps={{
        paper: {
          sx: { width: { xs: '100%', sm: 420 }, borderRadius: 0 },
        },
      }}
    >
      <div className='flex flex-col h-full'>
        <div className='flex items-start justify-between gap-3 px-6 pt-6 pb-4 border-b border-gray-200'>
          <div className='flex items-center gap-3'>
            <span className='flex items-center justify-center w-10 h-10 rounded-full bg-[var(--primary-color)]/10'>
              <LocalPrintshopIcon
                fontSize='small'
                sx={{ color: 'var(--primary-color)' }}
              />
            </span>
            <div className='flex flex-col'>
              <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                Etiquetas en un solo PDF
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {labelCountText(job.requestedCount)} para {actionNoun}
              </Typography>
            </div>
          </div>
          <IconButton size='small' onClick={onClose} aria-label='Cerrar'>
            <CloseIcon fontSize='small' />
          </IconButton>
        </div>

        <div className='flex flex-col gap-5 px-6 py-6 flex-1 overflow-y-auto'>
          {job.phase === 'running' && (
            <>
              <div className='flex items-center gap-3'>
                <CircularProgress
                  size={22}
                  sx={{ color: 'var(--primary-color)' }}
                />
                <div className='flex flex-col'>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    Generando tus etiquetas...
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Transcurrido: {formatElapsed(elapsedMs)}
                  </Typography>
                </div>
              </div>

              <LinearProgress
                sx={{
                  borderRadius: 999,
                  height: 6,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'var(--primary-color)',
                  },
                }}
              />

              <Typography variant='body2' color='text.secondary'>
                {canSeeJobs
                  ? 'Podés cerrar este panel y seguir trabajando: la generación continúa y el archivo queda disponible en Mis archivos.'
                  : 'Esto puede tardar unos segundos según la cantidad de etiquetas.'}
              </Typography>
            </>
          )}

          {job.phase === 'completed' && (
            <>
              <div className='flex items-center gap-3'>
                <TaskAltIcon color='success' />
                <div className='flex flex-col'>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    Listo, {labelCountText(generatedCount)} generadas
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    En {formatElapsed(Date.now() - job.startedAtMs)}
                  </Typography>
                </div>
              </div>

              {job.fileName && (
                <div className='rounded-lg border border-gray-200 bg-gray-50 px-4 py-3'>
                  <Typography variant='caption' color='text.secondary'>
                    Archivo
                  </Typography>
                  <Typography variant='body2' sx={{ wordBreak: 'break-all' }}>
                    {job.fileName}
                  </Typography>
                </div>
              )}

              {failedShippings.length > 0 && (
                <Alert severity='warning' sx={{ borderRadius: 2 }}>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {failedShippings.length === 1
                      ? 'Un pedido quedó sin etiqueta'
                      : `${failedShippings.length} pedidos quedaron sin etiqueta`}
                  </Typography>
                  <ul className='mt-1 max-h-40 overflow-y-auto pl-4 list-disc'>
                    {failedShippings.map((failed) => (
                      <li key={failed.shippingNumber}>
                        <Typography variant='caption'>
                          {failed.shippingNumber}: {failed.errorMessage}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}

              <Alert severity='info' sx={{ borderRadius: 2 }}>
                El PDF se abre en una pestaña nueva. Desde el visor podés
                imprimirlo o guardarlo.
              </Alert>
            </>
          )}

          {job.phase === 'errored' && (
            <>
              <div className='flex items-center gap-3'>
                <ErrorOutlineIcon color='error' />
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  No pudimos generar las etiquetas
                </Typography>
              </div>
              <Alert severity='error' sx={{ borderRadius: 2 }}>
                {job.errorMessage ?? 'Error desconocido'}
              </Alert>
            </>
          )}
        </div>

        <div className='flex flex-col gap-2 px-6 py-5 border-t border-gray-200'>
          {job.phase === 'completed' && job.downloadUrl && (
            <GlobalButton
              variant={GlobalButtonVariant.primaryButton}
              action={() =>
                window.open(job.downloadUrl!, '_blank', 'noopener,noreferrer')
              }
            >
              <OpenInNewIcon fontSize='small' />
              Abrir PDF de etiquetas
            </GlobalButton>
          )}

          {canSeeJobs ? (
            <GlobalButton
              variant={GlobalButtonVariant.secondaryButton}
              action={() => {
                onClose();
                navigate(jobsPath);
              }}
            >
              <FolderOpenIcon fontSize='small' />
              Ver en Mis archivos
            </GlobalButton>
          ) : (
            <GlobalButton
              variant={GlobalButtonVariant.secondaryButton}
              action={onClose}
            >
              Cerrar
            </GlobalButton>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default BatchLabelProgressDrawer;
